import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BarChart3, TrendingUp, Clock, CheckCircle, Download, FileSpreadsheet, 
  FileText, Calendar, RefreshCw, Users, MessageSquare, Hash,
  AlertCircle, Activity, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart, ComposedChart
} from 'recharts';
import { format, subDays, subMonths, subWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear } from 'date-fns';

const COLORS = ['#22C55E', '#F97316', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899'];

// Preset date ranges for export
const DATE_PRESETS = [
  { label: 'Today', getValue: () => ({ start: format(new Date(), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'Yesterday', getValue: () => ({ start: format(subDays(new Date(), 1), 'yyyy-MM-dd'), end: format(subDays(new Date(), 1), 'yyyy-MM-dd') }) },
  { label: 'Last 7 Days', getValue: () => ({ start: format(subDays(new Date(), 7), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'Last 14 Days', getValue: () => ({ start: format(subDays(new Date(), 14), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'Last 30 Days', getValue: () => ({ start: format(subDays(new Date(), 30), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'Last 90 Days', getValue: () => ({ start: format(subDays(new Date(), 90), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'This Week', getValue: () => ({ start: format(startOfWeek(new Date()), 'yyyy-MM-dd'), end: format(endOfWeek(new Date()), 'yyyy-MM-dd') }) },
  { label: 'Last Week', getValue: () => ({ start: format(startOfWeek(subWeeks(new Date(), 1)), 'yyyy-MM-dd'), end: format(endOfWeek(subWeeks(new Date(), 1)), 'yyyy-MM-dd') }) },
  { label: 'This Month', getValue: () => ({ start: format(startOfMonth(new Date()), 'yyyy-MM-dd'), end: format(endOfMonth(new Date()), 'yyyy-MM-dd') }) },
  { label: 'Last Month', getValue: () => ({ start: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'), end: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd') }) },
  { label: 'Last 6 Months', getValue: () => ({ start: format(subMonths(new Date(), 6), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'This Year', getValue: () => ({ start: format(startOfYear(new Date()), 'yyyy-MM-dd'), end: format(new Date(), 'yyyy-MM-dd') }) },
  { label: 'All Time', getValue: () => ({ start: '2020-01-01', end: format(new Date(), 'yyyy-MM-dd') }) },
];

const Analytics = ({ user, setUser }) => {
  const [comprehensiveData, setComprehensiveData] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [selectedPreset, setSelectedPreset] = useState('Last 30 Days');
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('area'); // 'area', 'line', 'bar'
  const canViewOrganizationAnalytics = user?.role === 'admin' && user?.is_master_admin !== true;
  const [analyticsScope, setAnalyticsScope] = useState('personal');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/analytics/comprehensive?start_date=${dateRange.start}&end_date=${dateRange.end}&scope=${analyticsScope}`);
      setComprehensiveData(res.data);
      try {
        const fb = await axiosInstance.get(`/analytics/feedback?start_date=${dateRange.start}&end_date=${dateRange.end}&scope=${analyticsScope}`);
        setFeedbackData(fb.data);
      } catch (e) {
        setFeedbackData(null);
      }
    } catch (error) {
      console.error('Failed to fetch analytics', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, analyticsScope]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Quick preset range handler
  const setPresetRange = (preset) => {
    const today = new Date();
    let start, end = format(today, 'yyyy-MM-dd');
    
    switch (preset) {
      case '7days':
        start = format(subDays(today, 7), 'yyyy-MM-dd');
        setSelectedPreset('Last 7 Days');
        break;
      case '30days':
        start = format(subDays(today, 30), 'yyyy-MM-dd');
        setSelectedPreset('Last 30 Days');
        break;
      case '90days':
        start = format(subDays(today, 90), 'yyyy-MM-dd');
        setSelectedPreset('Last 90 Days');
        break;
      case 'thisMonth':
        start = format(startOfMonth(today), 'yyyy-MM-dd');
        end = format(endOfMonth(today), 'yyyy-MM-dd');
        setSelectedPreset('This Month');
        break;
      case 'allTime':
        start = '2020-01-01';
        setSelectedPreset('All Time');
        break;
      default:
        start = format(subDays(today, 30), 'yyyy-MM-dd');
    }
    
    setDateRange({ start, end });
  };

  const handleDownload = async (fmt) => {
    setDownloading(fmt);
    try {
      const response = await axiosInstance.get(
        `/analytics/export/${fmt}?start_date=${dateRange.start}&end_date=${dateRange.end}&scope=${analyticsScope}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const extMap = { excel: 'xlsx', pdf: 'pdf', csv: 'csv', json: 'json' };
      const ext = extMap[fmt] || fmt;
      const contentDisposition = response.headers['content-disposition'];
      let filename = `qflow_analytics_${selectedPreset.replace(/\s/g, '_')}.${ext}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const applyPreset = (preset) => {
    setSelectedPreset(preset.label);
    setDateRange(preset.getValue());
  };

  // Prepare chart data
  const statusData = comprehensiveData ? [
    { name: 'Awaiting feedback', value: comprehensiveData.summary.pending_feedback, color: '#F59E0B' },
    { name: 'Resolved', value: comprehensiveData.summary.resolved, color: '#22C55E' },
    { name: 'Unresolved', value: comprehensiveData.summary.unresolved, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  const trendData = comprehensiveData?.daily_trends?.map(d => ({
    date: d.date.substring(5), // MM-DD format
    total: d.total,
    answered: d.answered,
    resolved: d.resolved,
  })) || [];

  const keywordData = comprehensiveData?.top_keywords?.slice(0, 10) || [];
  const userData = comprehensiveData?.user_activity || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex flex-col justify-center items-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <BarChart3 className="text-sky-600" size={32} />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600">{analyticsScope === 'organization' ? 'Combined statistics for every user in your organisation' : 'Your personal usage statistics, trends, and insights'}</p>
          </div>
          
          {/* Download Buttons */}
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => handleDownload('csv')}
              disabled={downloading === 'csv'}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="export-csv-btn"
            >
              {downloading === 'csv' ? (
                <RefreshCw className="animate-spin mr-2" size={18} />
              ) : (
                <FileText className="mr-2" size={18} />
              )}
              CSV
            </Button>
            <Button
              onClick={() => handleDownload('json')}
              disabled={downloading === 'json'}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="export-json-btn"
            >
              {downloading === 'json' ? (
                <RefreshCw className="animate-spin mr-2" size={18} />
              ) : (
                <FileText className="mr-2" size={18} />
              )}
              JSON
            </Button>
            <Button
              onClick={() => handleDownload('excel')}
              disabled={downloading === 'excel'}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {downloading === 'excel' ? (
                <RefreshCw className="animate-spin mr-2" size={18} />
              ) : (
                <FileSpreadsheet className="mr-2" size={18} />
              )}
              Excel
            </Button>
            <Button
              onClick={() => handleDownload('pdf')}
              disabled={downloading === 'pdf'}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {downloading === 'pdf' ? (
                <RefreshCw className="animate-spin mr-2" size={18} />
              ) : (
                <FileText className="mr-2" size={18} />
              )}
              PDF Report
            </Button>
          </div>
        </div>

        <Card className="p-4 mb-6" data-testid="analytics-scope-selector">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-gray-900">Analytics scope</p>
              <p className="text-sm text-gray-500">
                {analyticsScope === 'organization'
                  ? 'UI2 Tenant Admin view — includes every user in this organisation.'
                  : 'Personal view — includes only your own Qflow activity.'}
              </p>
            </div>
            {canViewOrganizationAnalytics ? <div className="inline-flex rounded-xl border bg-gray-50 p-1">
              <Button type="button" size="sm" variant={analyticsScope === 'personal' ? 'default' : 'ghost'} onClick={() => setAnalyticsScope('personal')} data-testid="analytics-scope-personal">
                My analytics
              </Button>
              <Button type="button" size="sm" variant={analyticsScope === 'organization' ? 'default' : 'ghost'} onClick={() => setAnalyticsScope('organization')} data-testid="analytics-scope-organization">
                Organisation summary
              </Button>
            </div> : <span className="inline-flex w-fit rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700">Personal analytics</span>}
          </div>
        </Card>

        {/* Date Range Filter */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="text-gray-500" size={20} />
              <span className="font-medium text-gray-700">Date Range:</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPresetRange('7days')}
                className="text-sm"
              >
                Last 7 Days
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPresetRange('30days')}
                className="text-sm"
              >
                Last 30 Days
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPresetRange('90days')}
                className="text-sm"
              >
                Last 90 Days
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPresetRange('thisMonth')}
                className="text-sm"
              >
                This Month
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setPresetRange('allTime')}
                className="text-sm"
              >
                All Time
              </Button>
            </div>
            
            <div className="flex items-center gap-2 ml-auto">
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-40"
              />
              <span className="text-gray-500">to</span>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-40"
              />
              <Button onClick={fetchAnalytics} variant="outline" size="sm">
                <RefreshCw size={16} className="mr-1" />
                Apply
              </Button>
            </div>
          </div>
        </Card>

        {error && (
          <Card className="p-4 mb-6 bg-red-50 border-red-200">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          </Card>
        )}

        {comprehensiveData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="p-6 bg-gradient-to-br from-sky-50 to-white border-sky-200" data-testid="analytics-total">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Total Queries</p>
                    <p className="text-3xl font-bold text-sky-700 mt-2">{comprehensiveData.summary.total}</p>
                  </div>
                  <div className="p-3 bg-sky-100 rounded-full">
                    <MessageSquare className="text-sky-600" size={28} />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-gradient-to-br from-green-50 to-white border-green-200" data-testid="analytics-resolved">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Answered</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">{comprehensiveData.summary.answered}</p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <CheckCircle className="text-green-600" size={28} />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-gradient-to-br from-orange-50 to-white border-orange-200" data-testid="analytics-open">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Resolved</p>
                    <p className="text-3xl font-bold text-orange-600 mt-2">{comprehensiveData.summary.resolved}</p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-full">
                    <Clock className="text-orange-600" size={28} />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 bg-gradient-to-br from-purple-50 to-white border-purple-200" data-testid="analytics-rate">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Resolution Rate</p>
                    <p className="text-3xl font-bold text-purple-600 mt-2">{Math.round(comprehensiveData.summary.resolution_rate)}%</p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <TrendingUp className="text-purple-600" size={28} />
                  </div>
                </div>
              </Card>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Status Distribution */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Hash className="text-sky-600" size={20} />
                  Query Status Distribution
                </h3>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-500">
                    No data available for selected period
                  </div>
                )}
              </Card>

              {/* Daily Trends */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="text-sky-600" size={20} />
                  Query Trends Over Time
                </h3>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="total" stackId="1" stroke="#3B82F6" fill="#93C5FD" name="Total" />
                      <Area type="monotone" dataKey="answered" stackId="2" stroke="#0EA5E9" fill="#BAE6FD" name="Answered" />
                      <Area type="monotone" dataKey="resolved" stackId="2" stroke="#22C55E" fill="#86EFAC" name="Resolved" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-gray-500">
                    No trend data available for selected period
                  </div>
                )}
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Top Keywords */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <MessageSquare className="text-sky-600" size={20} />
                  Most Asked Topics / Keywords
                </h3>
                {keywordData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={keywordData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="keyword" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-500">
                    No keyword data available
                  </div>
                )}
              </Card>

              {/* User Activity */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="text-sky-600" size={20} />
                  User Activity (Top 10)
                </h3>
                {userData.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {userData.map((u, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-semibold text-sm">
                            {idx + 1}
                          </div>
                          <span className="font-medium text-gray-800">{u.user}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">{u.queries} queries</span>
                          <span className="text-green-600">{u.resolved} resolved</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {u.answered > 0 ? Math.round((u.resolved / u.answered) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-500">
                    No user activity data available
                  </div>
                )}
              </Card>
            </div>

            {/* Top Queries Table */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Hash className="text-sky-600" size={20} />
                Most Frequently Asked Questions
              </h3>
              {comprehensiveData.top_queries?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Rank</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Query</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comprehensiveData.top_queries.map((q, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                              idx < 3 ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {idx + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-800">{q.query}</td>
                          <td className="py-3 px-4">
                            <span className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium">
                              {q.count}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  No query data available for selected period
                </div>
              )}
            </Card>

            {/* Monthly Trends */}
            {comprehensiveData.monthly_trends?.length > 0 && (
              <Card className="p-6 mt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="text-sky-600" size={20} />
                  Monthly Query Volume
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={comprehensiveData.monthly_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#8B5CF6" strokeWidth={2} dot={{ fill: '#8B5CF6' }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Answer Feedback Analytics */}
            {feedbackData && (
              <div className="mt-6 space-y-6" data-testid="feedback-analytics">
                {/* Feedback Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Helpful Feedback</p>
                    <p className="text-3xl font-bold text-emerald-600 mt-1">{feedbackData.summary?.helpful || 0}</p>
                  </Card>
                  <Card className="p-5 bg-gradient-to-br from-rose-50 to-white border-rose-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reports (Unsound)</p>
                    <p className="text-3xl font-bold text-rose-600 mt-1">{feedbackData.summary?.unsound || 0}</p>
                  </Card>
                  <Card className="p-5 bg-gradient-to-br from-slate-50 to-white border-slate-200">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Feedback</p>
                    <p className="text-3xl font-bold text-slate-700 mt-1">{feedbackData.summary?.total || 0}</p>
                  </Card>
                </div>

                {/* Most Reported Answers */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertCircle className="text-rose-600" size={20} />
                    Most Reported Answers
                  </h3>
                  {feedbackData.most_reported?.length > 0 ? (
                    <div className="space-y-3">
                      {feedbackData.most_reported.map((item, idx) => (
                        <div key={item.answer_id || idx} className="p-4 rounded-lg border border-rose-200 bg-rose-50/40" data-testid={`reported-answer-${idx}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-500 mb-1">Query</p>
                              <p className="text-sm text-gray-800 line-clamp-2">{item.query_text || '—'}</p>
                              <p className="text-xs font-medium text-gray-500 mt-3 mb-1">Answer</p>
                              <p className="text-sm text-gray-700 line-clamp-3 italic">&quot;{item.answer_text || '—'}&quot;</p>
                              {item.answer_author_name && (
                                <p className="text-xs text-gray-500 mt-2">Answered by {item.answer_author_name}</p>
                              )}
                              {item.comments?.length > 0 && (
                                <div className="mt-3 pl-3 border-l-2 border-rose-300">
                                  <p className="text-xs font-medium text-rose-700 mb-1">Reporter notes:</p>
                                  {item.comments.map((c, i) => (
                                    <p key={i} className="text-xs text-gray-600 italic">• {c}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold whitespace-nowrap">
                              {item.report_count} report{item.report_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No reported answers for selected period.</p>
                  )}
                </Card>

                {/* Feedback Trend */}
                {feedbackData.trend?.length > 0 && (
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Activity className="text-sky-600" size={20} />
                      Feedback Trend (Helpful vs Unsound)
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={feedbackData.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="helpful" fill="#10B981" name="Helpful" />
                        <Bar dataKey="unsound" fill="#F43F5E" name="Unsound" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
